import { useMemo } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  GEWERBE_FULL,
  WOHN_BASE,
  WOHN_FERIEN,
  labelFor,
  profileForType,
  type UnitFieldKind,
  type UnitProfile,
} from "./unitFollowUpSpec";
import UploadField from "./UploadField";
import type { Ansprache } from "./texte";
import { einheitSlotKey, type UploadScope } from "./uploadBuffer";
import { ziffernFilter } from "./GrossZahlEingabe";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  head: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    marginBottom: "10px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    fontFamily: BRAND.fontAccent,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  removeBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    fontWeight: 700,
    width: "26px",
    height: "26px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    ":hover": {
      backgroundColor: "#f5eef6",
      color: BRAND.purple,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.3,
    },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  radioRow: {
    display: "flex",
    columnGap: "16px",
    paddingTop: "4px",
  },
  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  radioInput: {
    accentColor: BRAND.purple,
    cursor: "pointer",
  },
  fullSpan: {
    "@media (min-width: 640px)": {
      gridColumn: "1 / -1",
    },
  },
  addBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "dashed", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    alignSelf: "flex-start",
    ":hover": {
      backgroundColor: "#f5eef6",
    },
  },
  intro: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    marginBottom: "8px",
  },
  empty: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
});

export type Einheit = Record<string, string>;

type Props = {
  kategorie: string | undefined;
  typ: string | undefined;
  einheiten: Einheit[];
  onChange: (next: Einheit[]) => void;
  countLockedFromTyp?: number;
  /** Ablage-Ebene der Mietverträge: die Gebäude-UUID. */
  scope: UploadScope;
  /**
   * Wird nach dem Löschen einer Einheit mit deren Index gerufen, damit der
   * Datei-Puffer die **index-basierten** Slots nachzieht. `Einheit` ist ein
   * `Record<string,string>` ohne stabile Id — ein künstliches `_uid` schlüge in
   * die JSON-serialisierten characteristics durch.
   */
  onUnitRemoved?: (idx: number) => void;
  /** Nur für den Hinweistext im Modus ohne Upload (E11). */
  ansprache?: Ansprache;
};

const profileForUnit = (
  kategorie: string | undefined,
  typ: string | undefined,
  unit: Einheit
): UnitProfile => {
  if (kategorie === "Mischnutzung (Wohnen u. Gewerbe)") {
    if (unit.mietWohnGewerbe === "Wohnen") {
      return { fields: WOHN_BASE };
    }
    if (unit.mietWohnGewerbe === "Gewerbe") {
      return { fields: ["mietWohnGewerbe", ...GEWERBE_FULL] as UnitFieldKind[] };
    }
    return { fields: ["mietWohnGewerbe"] as UnitFieldKind[] };
  }
  return profileForType(kategorie, typ);
};

/** Felder, die nur eine Zahl tragen dürfen — Buchstaben werden beim Tippen verworfen. */
const ZAHL_FELDER: ReadonlySet<UnitFieldKind> = new Set<UnitFieldKind>([
  "flaeche",
  "mieteinnahmen",
  "nebenkosten",
  "vorsteuerProzent",
  "mietereinbautenWert",
]);

const renderField = (
  kind: UnitFieldKind,
  unit: Einheit,
  setVal: (k: UnitFieldKind, v: string) => void,
  styles: ReturnType<typeof useStyles>
) => {
  // Yes/No fields
  if (
    kind === "vorsteuerJa" ||
    kind === "mietvertragJa" ||
    kind === "mietereinbautenJa" ||
    kind === "mietereinbautenMitversichern"
  ) {
    return (
      <div className={mergeClasses(styles.field, styles.fullSpan)}>
        <span className={styles.label}>{labelFor[kind]}</span>
        <div className={styles.radioRow}>
          {["ja", "nein"].map((opt) => (
            <label key={opt} className={styles.radioOption}>
              <input
                type="radio"
                className={styles.radioInput}
                checked={unit[kind] === opt}
                onChange={() => setVal(kind, opt)}
              />
              {opt === "ja" ? "Ja" : "Nein"}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (kind === "mietWohnGewerbe") {
    return (
      <div className={mergeClasses(styles.field, styles.fullSpan)}>
        <span className={styles.label}>{labelFor[kind]}</span>
        <div className={styles.radioRow}>
          {["Wohnen", "Gewerbe"].map((opt) => (
            <label key={opt} className={styles.radioOption}>
              <input
                type="radio"
                className={styles.radioInput}
                checked={unit[kind] === opt}
                onChange={() => setVal(kind, opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      </div>
    );
  }

  const isWide =
    kind === "mietvertragWelche" ||
    kind === "mietereinbautenWas" ||
    kind === "mietereinbautenBesitz" ||
    kind === "nutzung";

  return (
    <div className={mergeClasses(styles.field, isWide && styles.fullSpan)}>
      <span className={styles.label}>{labelFor[kind]}</span>
      <input
        className={styles.input}
        inputMode={ZAHL_FELDER.has(kind) ? "decimal" : undefined}
        value={unit[kind] ?? ""}
        onChange={(e) =>
          setVal(
            kind,
            ZAHL_FELDER.has(kind) ? ziffernFilter(e.target.value, { dezimal: true }) : e.target.value,
          )
        }
      />
    </div>
  );
};

const EinheitenList = ({
  kategorie,
  typ,
  einheiten,
  onChange,
  countLockedFromTyp,
  scope,
  onUnitRemoved,
  ansprache = "kunde",
}: Props) => {
  const styles = useStyles();
  const baseProfile = useMemo(
    () => profileForType(kategorie, typ),
    [kategorie, typ]
  );

  const lockedCount = countLockedFromTyp ?? baseProfile.countLocked;

  const setUnit = (idx: number, kind: UnitFieldKind, v: string) => {
    const next = einheiten.slice();
    next[idx] = { ...next[idx], [kind]: v };
    onChange(next);
  };

  const addEinheit = () => {
    onChange([...einheiten, {}]);
  };

  const removeEinheit = (idx: number) => {
    onChange(einheiten.filter((_, i) => i !== idx));
    // Erst die Liste, dann der Puffer — beide indizieren über dieselbe Position.
    onUnitRemoved?.(idx);
  };

  if (einheiten.length === 0 && !lockedCount) {
    return (
      <div>
        <p className={styles.intro}>Es ist noch keine Einheit erfasst.</p>
        <button type="button" className={styles.addBtn} onClick={addEinheit}>
          + Einheit hinzufügen
        </button>
      </div>
    );
  }

  // Special-case EFH/ZFH: no follow-up fields, just show a card per locked unit.
  const noFollowUps =
    baseProfile.fields.length === 0 && (lockedCount === 1 || lockedCount === 2);
  if (noFollowUps) {
    return (
      <p className={styles.empty}>
        Bei {typ ?? "diesem Gebäudetyp"} sind keine zusätzlichen Detail-Felder pro Einheit erforderlich. Sie können direkt zum nächsten Schritt.
      </p>
    );
  }

  return (
    <div>
      <p className={styles.intro}>
        Erfassen Sie jede Einheit mit ihren Eckdaten. Die Anzahl der Einheiten ergibt sich aus
        dieser Liste.
      </p>
      <div className={styles.list}>
        {einheiten.map((unit, idx) => {
          const profile = profileForUnit(kategorie, typ, unit);
          return (
            <div key={idx} className={styles.card}>
              <div className={styles.head}>
                <span className={styles.badge}>{idx + 1}</span>
                <span className={styles.title}>Einheit {idx + 1}</span>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => removeEinheit(idx)}
                  disabled={einheiten.length <= (lockedCount ?? 1)}
                  aria-label="Einheit entfernen"
                >
                  ×
                </button>
              </div>
              <div className={styles.grid}>
                {profile.fields.map((kind) => {
                  // Conditional reveal — vorsteuerProzent only if vorsteuerJa === "ja", etc.
                  if (kind === "vorsteuerProzent" && unit.vorsteuerJa !== "ja") return null;
                  if (kind === "mietvertragWelche" && unit.mietvertragJa !== "ja") return null;
                  if (
                    (kind === "mietereinbautenWas" ||
                      kind === "mietereinbautenWert" ||
                      kind === "mietereinbautenBesitz" ||
                      kind === "mietereinbautenMitversichern") &&
                    unit.mietereinbautenJa !== "ja"
                  ) {
                    return null;
                  }
                  return (
                    <div
                      key={kind}
                      className={mergeClasses(
                        (kind === "nutzung" ||
                          kind === "mietWohnGewerbe" ||
                          kind === "vorsteuerJa" ||
                          kind === "mietvertragJa" ||
                          kind === "mietvertragWelche" ||
                          kind === "mietereinbautenJa" ||
                          kind === "mietereinbautenWas" ||
                          kind === "mietereinbautenBesitz" ||
                          kind === "mietereinbautenMitversichern") &&
                          styles.fullSpan,
                      )}
                    >
                      {renderField(kind, unit, (k, v) => setUnit(idx, k, v), styles)}
                    </div>
                  );
                })}
                {unit.mietvertragJa === "ja" && (
                  <div className={styles.fullSpan}>
                    <UploadField
                      scope={scope}
                      slotKey={einheitSlotKey(idx)}
                      label={`Mietvertrag Einheit ${idx + 1}`}
                      ansprache={ansprache}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12 }}>
        <button type="button" className={styles.addBtn} onClick={addEinheit}>
          + Weitere Einheit hinzufügen
        </button>
      </div>
      {/* Avoid unused-import warning when WOHN_FERIEN exported from spec is not directly referenced here */}
      <span style={{ display: "none" }}>{WOHN_FERIEN.length}</span>
    </div>
  );
};

export default EinheitenList;
